import {
  facilityAddonIconId,
  type FacilityIconId,
} from '../utils/assetFacilityIcons';
import { ASSISTANT_FACILITY_AMENITY_LABELS } from './assistantFacilityAmenityLabels';
import { normalizeVoiceText, removeVoicePhrase } from './matchVoiceSearchCity';

type AmenityPhraseEntry = { phrases: string[]; icon: FacilityIconId };

const HEBREW_CHAR = /[\u0590-\u05FF]/;
const AMENITY_PREFIXES_HE = ['עם', 'שיש', 'שיש בו', 'כולל', 'שיש בה'] as const;
const AMENITY_PREFIXES_EN = ['with a', 'with', 'including', 'that has', 'has'] as const;

/** Extra spoken forms that are not exact catalog labels. */
const EXTRA_SPOKEN_PHRASES: Array<{ phrases: string[]; icon: FacilityIconId }> = [
  {
    icon: 'wifi',
    phrases: ['עם wifi', 'עם wi-fi', 'עם ווייפיי', 'עם ווי פיי', 'wifi', 'wi-fi'],
  },
  {
    icon: 'parking',
    phrases: ['עם פרקינג', 'פרקינג'],
  },
  {
    icon: 'kitchen',
    phrases: ['kitchenette', 'עם מטבחון', 'מטבחון'],
  },
  {
    icon: 'cafe',
    phrases: ['with coffee', 'coffee'],
  },
  {
    icon: 'transit',
    phrases: ['עם תחבורה', 'near public transport', 'near transit'],
  },
  {
    icon: 'accessibility',
    phrases: ['accessible', 'wheelchair', 'נגיש'],
  },
  {
    icon: 'ev',
    phrases: ['ev charger', 'charging station', 'טעינה לרכב'],
  },
  {
    icon: 'bicycle',
    phrases: ['bike parking', 'חניה לאופניים'],
  },
];

function isMostlyHebrew(text: string): boolean {
  return HEBREW_CHAR.test(text);
}

function phrasesForLabel(label: string): string[] {
  const trimmed = label.trim();
  if (!trimmed) {
    return [];
  }
  const out = new Set<string>([trimmed]);
  if (isMostlyHebrew(trimmed)) {
    for (const prefix of AMENITY_PREFIXES_HE) {
      out.add(`${prefix} ${trimmed}`);
    }
  } else {
    for (const prefix of AMENITY_PREFIXES_EN) {
      out.add(`${prefix} ${trimmed}`);
    }
  }
  return Array.from(out);
}

function buildAmenityPhraseEntries(): AmenityPhraseEntry[] {
  const byIcon = new Map<FacilityIconId, Set<string>>();
  const add = (icon: FacilityIconId, phrases: readonly string[]) => {
    if (icon === 'default') {
      return;
    }
    let set = byIcon.get(icon);
    if (!set) {
      set = new Set<string>();
      byIcon.set(icon, set);
    }
    for (const phrase of phrases) {
      const n = normalizeVoiceText(phrase);
      if (n.length >= 2) {
        set.add(phrase.trim());
      }
    }
  };

  for (const label of ASSISTANT_FACILITY_AMENITY_LABELS) {
    add(facilityAddonIconId(label), phrasesForLabel(label));
  }
  for (const entry of EXTRA_SPOKEN_PHRASES) {
    add(entry.icon, entry.phrases);
  }

  const entries: AmenityPhraseEntry[] = [];
  Array.from(byIcon.entries()).forEach(([icon, phrases]) => {
    entries.push({
      icon,
      phrases: Array.from(phrases).sort((a, b) => b.length - a.length),
    });
  });
  // Longer first phrase across icons first so “מכונת קפה” wins over “קפה”.
  entries.sort((a, b) => (b.phrases[0]?.length ?? 0) - (a.phrases[0]?.length ?? 0));
  return entries;
}

const AMENITY_PHRASES = buildAmenityPhraseEntries();

export type AssistantAmenityParse = {
  icons: FacilityIconId[];
  remainder: string;
};

/** Strip amenity phrases; return facility icon ids for hard refine. */
export function parseAssistantAmenity(text: string): AssistantAmenityParse {
  let remainder = normalizeVoiceText(text);
  if (!remainder) {
    return { icons: [], remainder: '' };
  }
  const icons: FacilityIconId[] = [];
  const seen = new Set<FacilityIconId>();
  for (const entry of AMENITY_PHRASES) {
    for (const phrase of entry.phrases) {
      const needle = normalizeVoiceText(phrase);
      if (!needle || !remainder.includes(needle)) {
        continue;
      }
      remainder = removeVoicePhrase(remainder, needle);
      if (!seen.has(entry.icon)) {
        seen.add(entry.icon);
        icons.push(entry.icon);
      }
      break;
    }
  }
  return { icons, remainder };
}

/** Facility icons the assistant can hard-filter (for tests / docs). */
export function assistantAmenityFilterIcons(): FacilityIconId[] {
  return AMENITY_PHRASES.map((entry) => entry.icon);
}
